/**
 * useFilePicker — SSOT hook for web file/folder picking and drag-drop.
 *
 * Wraps the hidden <input> elements and exposes clean imperatives:
 *   openFilePicker()   → triggers the file-selection dialog
 *   openFolderPicker() → triggers the folder-selection dialog
 *
 * Also handles drag-drop events via handleDrop().
 *
 * Usage:
 *   const { openFilePicker, openFolderPicker, handleDrop, inputs } = useFilePicker({ onFiles });
 *   // Render inputs somewhere in the tree (usually in overlays):
 *   {inputs}
 */
import React, { useRef } from 'react';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface UseFilePickerOptions {
  /** Called whenever the user selects or drops files. */
  onFiles: (files: File[]) => void;
}

export interface FilePickerControls {
  openFilePicker:   () => void;
  openFolderPicker: () => void;
  handleDrop:       (fileList: FileList | null) => void;
  /** Hidden <input> elements — render these once in your overlay area. */
  inputs:           React.ReactNode;
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

export function useFilePicker({ onFiles }: UseFilePickerOptions): FilePickerControls {
  const fileInputRef   = useRef<HTMLInputElement>(null);
  const folderInputRef = useRef<HTMLInputElement>(null);

  const openFilePicker   = () => fileInputRef.current?.click();
  const openFolderPicker = () => folderInputRef.current?.click();

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []) as File[];
    if (files.length) onFiles(files);
    // Reset input value so selecting the same file again still fires onChange
    e.target.value = '';
  };

  const handleDrop = (fileList: FileList | null) => {
    const files = Array.from(fileList ?? []) as File[];
    if (files.length) onFiles(files);
  };

  // Only rendered on web — native builds will never hit this code path
  const inputs = typeof window !== 'undefined' ? (
    <>
      <input
        type="file"
        ref={fileInputRef}
        multiple
        onChange={handleChange}
        style={{ display: 'none' }}
      />
      <input
        type="file"
        ref={folderInputRef}
        // @ts-ignore — non-standard but universally supported
        webkitdirectory=""
        directory=""
        onChange={handleChange}
        style={{ display: 'none' }}
      />
    </>
  ) : null;

  return { openFilePicker, openFolderPicker, handleDrop, inputs };
}
